import * as React from "react";
import { reaction } from "mobx";
import qs from "qs";
import { Tab, Tabs, Button, Intent, ButtonGroup, PopoverInteractionKind } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import Router from "next/router";
import GenomesTable from "./table";
import { FEATURE_FLAGS, StoresContext } from "../../../pages/_app";
import SearchSuggestionBox from "../../search-suggestion-box";
import UnexpectedBackendError from "../../backend-error";
import PageHeader from "../../page-header";
import { trackSegmentEvent } from "../../../utils/analytics";
import { getMicrobeSearchSections } from "../../../utils/search";
import { CollectionName, GenomesOrderBy, Genome } from "../../../api";
import { useContext, useEffect } from "react";
import { observer } from "mobx-react";
import { NumberOfSelectedItems } from "./number-of-selected-label";
import { DownloadSelectedButton } from "./download-selected-button";
import { NoResults } from "./no-results";
import { DisplayKind } from "../../../stores/genome-store";
import { SequenceSearchTextarea } from "../../sequence-search-textarea";
import GenomesTree from "./tree";
import { UnsequencedProductsSwitch } from "./tree/unsequenced-products-switch";
import GenomeSearchTags from "../../genome-search-tags";

type Props = {
  collectionName: CollectionName | null;
  showDisplayAsTreeOption?: boolean;
};

export const GenomesListPage = ({ collectionName }: Props) => {
  const pageName = collectionName ? `Genomes: ${collectionName}` : "Genomes";
  return (
    <>
      <PageHeader name={pageName} capitalize={true} />
      <GenomesList collectionName={collectionName} />
    </>
  );
};

export const GenomesList = observer(
  ({
    collectionName,
    relatedGenome,
    showDisplayAsTreeOption = true,
  }: Props & { relatedGenome?: Genome }) => {
    const rootStore = useContext(StoresContext);

    useEffect(() => {
      // Loading data from the client
      const qsp = decodeURIComponent(window.location.search.substring(1));
      const params = qs.parse(qsp);

      if (relatedGenome) {
        params.related_genome = relatedGenome.uuid;
      }

      rootStore.genomeStore.updateIfNotHydrated(collectionName, params);

      let url = "";
      if (relatedGenome) {
        url = `/genomes/${relatedGenome.uuid}`;
      } else if (collectionName) {
        url = `/genomes/${collectionName}`;
      } else {
        url = "/genomes";
      }

      const stopReacting = reaction(
        () => rootStore.genomeStore.urlParams.toString(),
        () => {
          // When a variable changes, that means we're re-fetching data. Scroll back up and update
          // URL
          window.scrollTo(0, 0);
          const urlParams = rootStore.genomeStore.urlParams;
          const tab = relatedGenome
            ? new URLSearchParams(window.location.search).get("tab")
            : undefined;
          if (tab) {
            urlParams.set("tab", tab);
          }

          if (!urlParams.toString()) {
            Router.push(url, url, { shallow: true });
          } else if (window.location.search !== `?${urlParams}`) {
            Router.push(`${url}?${urlParams}`, `${url}?${urlParams}`, {
              shallow: true,
            });
          }
        }
      );
      return stopReacting;
    }, []);

    const setOrderBy = (orderBy: GenomesOrderBy) => {
      trackSegmentEvent("Sort Genomes List", {
        orderTerm: orderBy,
        context: "Genomes List",
      });
      rootStore.genomeStore.setOrderBy(orderBy);
    };

    const filterByTag = (tag: string) => {
      if (rootStore.genomeStore.searchObject.tags.indexOf(tag) !== -1) {
        return;
      }
      rootStore.genomeStore.addSearchFilter("tags", tag);
    };

    const changePage = (page: number) => {
      rootStore.genomeStore.setPage(page);
    };

    const toggleGenomeStarring = (starred: boolean, uuid: string) => {
      rootStore.genomeStore.toggleGenomeStarring(starred, uuid);
    };

    const renderTable = () => {
      const { loading, pagination, genomes, error } = rootStore.genomeStore.data;
      const { orderBy, ascending } = rootStore.genomeStore;

      if (error) {
        return <UnexpectedBackendError />;
      }

      if (loading) {
        return (
          <GenomesTable
            collectionName={collectionName}
            genomes={[]}
            totalCount={0}
            orderBy={orderBy}
            page={pagination.page!}
            loading={true}
            changePage={changePage}
            filterByTag={filterByTag}
            toggleGenomeStarring={toggleGenomeStarring}
            changeOrderBy={setOrderBy}
            isAscending={ascending}
          />
        );
      }

      if (!loading && pagination.total === 0) {
        return <NoResults />;
      }

      return (
        <GenomesTable
          collectionName={collectionName}
          genomes={genomes}
          totalCount={pagination.total!}
          orderBy={orderBy}
          page={pagination.page!}
          loading={false}
          changePage={changePage}
          filterByTag={filterByTag}
          toggleGenomeStarring={toggleGenomeStarring}
          changeOrderBy={setOrderBy}
          isAscending={ascending}
        />
      );
    };

    const { numSelected, numTotal } = rootStore.genomeStore.getNumSelected();

    const renderTab = () => {
      return (
        <div className="genomes-table-container">
          <SequenceSearchTextarea />
          <div className="genomes-table-container__meta">
            <div className="genomes-table-container__meta__controls">
              <div className="genomes-table-container__download-button">
                <DownloadSelectedButton
                  downloadInfos={[
                    { type: "assembly" },
                    { type: "annotations" },
                    { type: "epigenome" },
                    { type: "resistome" },
                    { type: "supplemental_data" },
                  ]}
                  initBulkSearch={rootStore.genomeStore.initBulkSearch}
                  initBulkDownload={rootStore.genomeStore.initBulkDownload}
                  hasRequiredSubscription={rootStore.userStore.hasActiveMicrobeSubscription}
                  disabled={numSelected === 0}
                />
              </div>
              {rootStore.genomeStore.displayKind === "table" ? (
                <NumberOfSelectedItems
                  numSelected={numSelected}
                  numTotal={numTotal}
                  onSelectAll={rootStore.genomeStore.selectAll}
                  onDeselectAll={rootStore.genomeStore.deselectAll}
                />
              ) : (
                <UnsequencedProductsSwitch />
              )}
            </div>
            <div className="genomes-table-container__meta__search">
              <GenomeSearchTags
                className="genomes-table-container__meta__search__tags"
                inUseSearchFields={rootStore.genomeStore.inUseSearchFields}
                removeSearchFilter={rootStore.genomeStore.removeSearchFilter}
              />
              <div className="genomes-table-container__meta__search__box">
                <SearchSuggestionBox
                  performSearch={rootStore.genomeStore.addSearchFilter}
                  searchSections={getMicrobeSearchSections(
                    rootStore.genomeStore.displayingTreeWithShowUnsequenced
                  )}
                />
              </div>
            </div>
            {showDisplayAsTreeOption && (
              <div className="genomes-table-container__meta__display">
                <TreeTableSwitcher
                  value={rootStore.genomeStore.displayKind}
                  onChange={rootStore.genomeStore.setDisplayKind}
                />
                <NewArrow />
              </div>
            )}
          </div>
          {rootStore.genomeStore.displayKind === "table" ? (
            <div className="genomes-table-container__table">{renderTable()}</div>
          ) : null}
          {rootStore.genomeStore.displayKind === "tree" ? (
            <div className="genomes-table-container__tree">
              <GenomesTree />
            </div>
          ) : null}
        </div>
      );
    };

    const tabContent = renderTab();
    return (
      <div className="genomes-list">
        <Tabs
          id="genomes-tabs"
          defaultSelectedTabId={
            rootStore.genomeStore.mineOnly ? "my-genomes-tab" : "all-genomes-tab"
          }
          renderActiveTabPanelOnly={true}
          animate={false}
          onChange={(name: string) => {
            rootStore.genomeStore.setMineOnly(name === "my-genomes-tab");
          }}
        >
          <Tab title="All Microbes" id="all-genomes-tab" panel={tabContent} />
          {rootStore.userStore.isLoggedIn() && (
            <Tab title="My Favorite Microbes" id="my-genomes-tab" panel={tabContent} />
          )}
          <Tabs.Expander />
        </Tabs>
      </div>
    );
  }
);

const NewArrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={"160px"}
      height={"100px"}
      className="genomes-table-container__meta__display__new-arrow"
    >
      <rect width="40" height="20" x="30" y="20" rx="5" />
      <text x={"50"} y={"31"} dominantBaseline="middle" textAnchor="middle">
        New
      </text>
      <path d="M 70,30 C 100,30,144,30,144,94" />
      <path d="M150,88 L144,94 L138,88" />
    </svg>
  );
};

type TreeTableSwitcherProps = { value: DisplayKind; onChange: (newValue: DisplayKind) => void };
const TreeTableSwitcher = observer(({ value, onChange }: TreeTableSwitcherProps) => {
  if (!FEATURE_FLAGS.CatalogTree) {
    return null;
  }
  return (
    <ButtonGroup>
      <Popover2
        interactionKind={PopoverInteractionKind.HOVER}
        placement={"top"}
        content={"Display as table"}
        popoverClassName="info-tooltip info-tooltip--small"
      >
        <Button
          rightIcon={"list-columns"}
          intent={value === "table" ? Intent.PRIMARY : Intent.NONE}
          onClick={() => onChange("table")}
        />
      </Popover2>
      <Popover2
        interactionKind={PopoverInteractionKind.HOVER}
        placement={"top"}
        content={"Display as tree"}
        popoverClassName="info-tooltip info-tooltip--small"
      >
        <Button
          rightIcon={"diagram-tree"}
          intent={value === "tree" ? Intent.PRIMARY : Intent.NONE}
          onClick={() => onChange("tree")}
        />
      </Popover2>
    </ButtonGroup>
  );
});
